/**
 * RadarViewPage Component
 *
 * Individual employee radar view with tabs:
 * - Individual: Personal performance metrics
 * - Leader: Team metrics (if employee is a manager)
 * - Matrix: Detailed evaluation breakdown
 *
 * Also handles comparison mode via ?compare= query param.
 */
import React from 'react'
import { useParams, useSearch, useNavigate, Link } from '@tanstack/react-router'
import { ChevronRight, GitCompare, Star, Users, Mail, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/Card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { useRadar, useEvaluationMatrix } from '@/hooks/queries/useRadarQueries'
import { useRadarUIStore } from '@/stores/radar-ui-store'
import { PerformanceScoreBadge } from './PerformanceScoreBadge'
import { EngagementIndicator } from './EngagementIndicator'
import { IndividualMetricsPanel } from './IndividualMetricsPanel'
import { LeaderMetricsPanel } from './LeaderMetricsPanel'
import { EvaluationMatrixPanel, EvaluationMatrixSkeleton } from './EvaluationMatrixPanel'
import { ComparisonView } from './ComparisonView'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { RadarTab } from '@/types/radar'

export function RadarViewPage() {
  const { employeeId } = useParams({ from: '/_auth/performance/radar/$employeeId/' })
  const search = useSearch({ from: '/_auth/performance/radar/$employeeId/' })
  const navigate = useNavigate()

  // Hidden: favorites and comparison features temporarily disabled
  // const { getCurrentPeriod, favorites, toggleFavorite, openCompareModal } = useRadarUIStore()
  const { getCurrentPeriod } = useRadarUIStore()
  const period = getCurrentPeriod()

  const { data, isLoading, isError, error } = useRadar(employeeId, period)
  const { data: matrixData, isLoading: isMatrixLoading } = useEvaluationMatrix(
    search.tab === 'matrix' ? employeeId : null
  )

  const activeTab = search.tab || 'individual'
  const compareId = search.compare

  // Handle tab change via URL
  const handleTabChange = (tab: string) => {
    navigate({
      to: '/performance/radar/$employeeId',
      params: { employeeId },
      search: { tab: tab as RadarTab, compare: compareId },
    })
  }

  // Hidden: comparison feature temporarily disabled
  // If comparison mode, render comparison view
  // if (compareId) {
  //   return <ComparisonView employeeIdA={employeeId} employeeIdB={compareId} />
  // }

  // Loading state
  if (isLoading) {
    return <RadarViewSkeleton />
  }

  // Error state
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <User className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Erro ao carregar radar</h2>
        <p className="text-brand-gray mb-4">
          {error instanceof Error ? error.message : 'Funcionário não encontrado ou sem permissão de acesso.'}
        </p>
        <Link
          to="/performance/radar"
          className="text-brand-primary hover:underline"
        >
          Voltar para a visão geral
        </Link>
      </div>
    )
  }

  if (!data) {
    return null
  }

  // Validate required data structure
  if (!data.employee || !data.individual || !data.metadata) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <User className="w-12 h-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Dados incompletos</h2>
        <p className="text-brand-gray mb-4">
          A API retornou dados incompletos para este funcionário.
        </p>
        <Link
          to="/performance/radar"
          className="text-brand-primary hover:underline"
        >
          Voltar para a visão geral
        </Link>
      </div>
    )
  }

  const { employee, individual, asLeader, directReports, metadata } = data
  // Hidden: favorites feature temporarily disabled
  // const isFavorite = favorites.includes(employee.employeeId)
  const lastUpdated = formatDistanceToNow(new Date(metadata.generatedAt), {
    addSuffix: true,
    locale: ptBR,
  })

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-brand-gray">
        <Link to="/performance/radar" className="hover:text-white">
          Radar
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-white">{employee.name}</span>
      </nav>

      {/* Employee Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            {/* Avatar and basic info */}
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-brand-gray/30 flex items-center justify-center flex-shrink-0">
                {employee.pictureUrl ? (
                  <img
                    src={employee.pictureUrl}
                    alt={employee.name}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-3xl font-medium text-white">
                    {employee.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{employee.name}</h2>
                <p className="text-brand-gray">{employee.role}</p>
                <p className="text-brand-gray text-sm flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {employee.email}
                </p>
                {employee.managerId && (
                  <p className="text-brand-gray text-sm">
                    Gestor: <Link
                      to="/performance/radar/$employeeId"
                      params={{ employeeId: employee.managerId }}
                      className="text-brand-primary hover:underline"
                    >
                      Ver radar do gestor
                    </Link>
                  </p>
                )}
              </div>
            </div>

            {/* Hidden: Actions - favorites and comparison features temporarily disabled
            <div className="flex items-center gap-2 md:ml-auto">
              <button
                onClick={() => toggleFavorite(employee.employeeId)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors',
                  isFavorite
                    ? 'border-yellow-400/50 bg-yellow-400/10 text-yellow-400'
                    : 'border-brand-gray/30 text-brand-gray hover:text-yellow-400 hover:border-yellow-400/50'
                )}
              >
                <Star className="w-4 h-4" fill={isFavorite ? 'currentColor' : 'none'} />
                {isFavorite ? 'Favorito' : 'Favoritar'}
              </button>
              <button
                onClick={openCompareModal}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-brand-gray/30 text-brand-gray hover:text-white hover:border-brand-gray/50 transition-colors"
              >
                <GitCompare className="w-4 h-4" />
                Comparar
              </button>
            </div>
            */}
          </div>

          {/* Quick metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-brand-gray/20">
            <div className="text-center">
              <PerformanceScoreBadge
                score={individual.evaluations.overall}
                size="lg"
              />
              <p className="text-xs text-brand-gray mt-1">Performance</p>
            </div>
            <div className="text-center">
              <span className="text-2xl font-bold text-white">
                {Math.round(individual.okrs.completionRate)}%
              </span>
              <p className="text-xs text-brand-gray mt-1">OKR</p>
            </div>
            <div className="text-center">
              <span className="text-2xl font-bold text-white">
                {individual.feedback.ratio.toFixed(1)}:1
              </span>
              <p className="text-xs text-brand-gray mt-1">Feedback Ratio</p>
            </div>
            {/* Hidden: Engagement indicator - feature temporarily disabled
            <div className="text-center">
              <EngagementIndicator
                signal={individual.engagement.overallSignal}
                showLabel
                size="lg"
              />
              <p className="text-xs text-brand-gray mt-1">Engajamento</p>
            </div>
            */}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="individual">Individual</TabsTrigger>
          {asLeader && (
            <TabsTrigger value="leader">
              <Users className="w-4 h-4 mr-1" />
              Como Líder
            </TabsTrigger>
          )}
          <TabsTrigger value="matrix">Matriz de Avaliação</TabsTrigger>
        </TabsList>

        <TabsContent value="individual">
          <IndividualMetricsPanel metrics={individual} />
        </TabsContent>

        {asLeader && (
          <TabsContent value="leader">
            <LeaderMetricsPanel
              metrics={asLeader}
              directReports={directReports}
              employeeId={employeeId}
            />
          </TabsContent>
        )}

        <TabsContent value="matrix">
          {isMatrixLoading ? (
            <EvaluationMatrixSkeleton />
          ) : matrixData ? (
            <EvaluationMatrixPanel data={matrixData} />
          ) : (
            <div className="text-center py-12 text-brand-gray">
              <p>Dados de avaliação não disponíveis para este funcionário.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Footer info */}
      <div className="flex items-center justify-between text-sm text-brand-gray">
        <span>Completude dos dados: {metadata.dataCompleteness}%</span>
        <span>Última atualização: {lastUpdated}</span>
      </div>
    </div>
  )
}

/**
 * Loading skeleton
 */
function RadarViewSkeleton() {
  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Skeleton className="h-5 w-48 bg-brand-gray/20" />

      {/* Header card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex items-center gap-4">
              <Skeleton className="w-20 h-20 rounded-full bg-brand-gray/20" />
              <div className="space-y-2">
                <Skeleton className="h-8 w-48 bg-brand-gray/20" />
                <Skeleton className="h-5 w-32 bg-brand-gray/20" />
                <Skeleton className="h-4 w-56 bg-brand-gray/20" />
              </div>
            </div>
            <div className="flex gap-2 md:ml-auto">
              <Skeleton className="h-10 w-24 bg-brand-gray/20 rounded-lg" />
              <Skeleton className="h-10 w-28 bg-brand-gray/20 rounded-lg" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-brand-gray/20">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="text-center">
                <Skeleton className="h-8 w-16 mx-auto bg-brand-gray/20" />
                <Skeleton className="h-3 w-12 mx-auto mt-2 bg-brand-gray/20" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Skeleton className="h-10 w-80 bg-brand-gray/20 rounded-lg" />

      {/* Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-64 bg-brand-gray/20 rounded-xl" />
        ))}
      </div>
    </div>
  )
}
